import {Component, OnInit, ViewChild} from '@angular/core';
import {StripePaymentElementComponent, StripeService} from 'ngx-stripe';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ResponseSnackbarService} from '../../../../shared/services/response-snackbar.service';
import {GeneralConstant} from '../../../../shared/constants/GeneralConstant';
import {PaymentMethodService} from '../../../../shared/services/payment-method.service';
import {ConfirmModalComponent} from '../../../shared/modules/confirm-modal/confirm-modal.component';
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'app-stripe-add-card',
  templateUrl: './stripe-add-card.component.html',
  styleUrls: ['./stripe-add-card.component.scss']
})
export class StripeAddCardComponent implements OnInit {
  @ViewChild(StripePaymentElementComponent) paymentElement: StripePaymentElementComponent;

  appearance = {
    theme: 'stripe',
    labels: 'floating'
  };

  saveBtnLoading = false;
  confirmBtnDisabled = true;
  clientSecret: string;
  verificationUrl: string;
  selectedType: string;

  constructor(
    private paymentMethodService: PaymentMethodService,
    private stripeService: StripeService,
    public dialogRef: MatDialogRef<StripeAddCardComponent>,
    private snackBar: ResponseSnackbarService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.createSetupIntent();
  }

  cancel(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        fullName: 'Warning',
        name: '',
        type: 'Confirm',
        removeQuestionMark: true,
        content: 'You have unsaved changes. If you close the pop-up window, these changes will be lost.'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.delete) {
        this.dialogRef.close();
      }
    });
  }

  onChange(event: any): void {
    this.selectedType = event?.value?.type;
    this.confirmBtnDisabled = !event.complete;
  }

  onSubmit(): void {
    if (!this.paymentElement?.elements || this.confirmBtnDisabled) {
      return;
    }

    this.saveBtnLoading = true;
    this.confirmBtnDisabled = true;

    this.stripeService.confirmSetup({
      elements: this.paymentElement.elements,
      redirect: 'if_required'
    }).pipe(
      finalize(() => {
        this.saveBtnLoading = false;
        this.confirmBtnDisabled = false;
      })
    ).subscribe(result => {
      if (result.error) {
        this.snackBar.onResponseMessage(result.error.message, GeneralConstant.SNACKBAR_RES_ERROR_CLASS);
        return;
      }

      const setupIntent = result.setupIntent;
      const nextAction = setupIntent?.next_action as any;
      if (setupIntent?.status === 'requires_action'
        && nextAction?.type === 'verify_with_microdeposits'
        && nextAction?.verify_with_microdeposits?.hosted_verification_url) {
        this.verificationUrl = nextAction.verify_with_microdeposits.hosted_verification_url;
        this.snackBar.onResponseMessage(
          'Bank account added. Complete verification from Payment Methods when the deposits arrive.',
          GeneralConstant.SNACKBAR_RES_SUCCESS_CLASS
        );
        this.dialogRef.close(true);
        return;
      }

      if (setupIntent?.status === 'succeeded') {
        const isBankAccount = this.selectedType === 'us_bank_account';
        this.snackBar.onResponseMessage(
          isBankAccount ? 'Bank account saved successfully.' : 'Payment method saved successfully.',
          GeneralConstant.SNACKBAR_RES_SUCCESS_CLASS
        );
        this.dialogRef.close(true);
      }
    });
  }

  openVerificationUrl(): void {
    if (this.verificationUrl) {
      window.open(this.verificationUrl, '_blank');
      this.dialogRef.close(true);
    }
  }

  private createSetupIntent(): void {
    this.saveBtnLoading = true;
    this.paymentMethodService.createSetupIntent('all').pipe(
      finalize(() => {
        this.saveBtnLoading = false;
      })
    ).subscribe(response => {
      this.clientSecret = response.clientSecret;
    }, error => {
      const message = error?.error?.message || 'Unable to start payment method setup.';
      this.snackBar.onResponseMessage(message, GeneralConstant.SNACKBAR_RES_ERROR_CLASS);
      this.dialogRef.close();
    });
  }
}
